import {useEffect, useRef} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {DownloadStateTypes, updateOrderStateNew, type OrderDownloadStateNew} from '@Components/download-panel/download-panel.reducer';
import {
  getMultipleOrdersProgress,
  POLLING_INTERVAL,
  MAX_POLLING_ATTEMPTS,
  filterOrdersByOptions,
  createReadyStateUpdate,
  createErrorStateUpdate,
  createDownloadingStateUpdate,
  triggerDownloadWithDelay,
  type MultipleOrderProgressResponse,
} from '@Libraries/poster-download-library';
import {makeFilenameSafe} from '@Utils/file.util';
import {saveToGoogleDriveWithSelectedFolder} from '@Libraries/google-drive-download-picker';
import {showEditorNotificationMessageGrowl} from '@Components/poster-editor/library/poster-editor-library';
import {isUserPremium} from '@Libraries/user.library';

export const usePremiumDownloads = (): void => {
  const dispatch = useAppDispatch();
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingAttemptsRef = useRef<Record<string, number>>({});
  const downloadTriggeredRef = useRef<Record<string, boolean>>({});
  const isPollingRef = useRef<boolean>(false);

  const orderStateHashmapNew = useAppSelector((state) => state.downloadDialog.orderStateHashmapNew);
  const currentlyProcessingOrderIdKeys = useAppSelector((state) => state.downloadDialog.currentlyProcessingOrderIdKeys);
  const pollingTrigger = useAppSelector((state) => state.downloadDialog.pollingTrigger);

  const getLatestState = useRef(() => ({
    orderStateHashmapNew,
    currentlyProcessingOrderIdKeys,
  }));

  getLatestState.current = () => ({
    orderStateHashmapNew,
    currentlyProcessingOrderIdKeys,
  });

  const stillDownloadingOrdersRef = useRef<Set<string>>(new Set());

  const getOrdersToPoll = (): OrderDownloadStateNew[] => {
    const {orderStateHashmapNew: currentHashmap, currentlyProcessingOrderIdKeys: currentKeys} = getLatestState.current!();
    const allOrders = currentKeys.map((orderIdKey) => currentHashmap[orderIdKey]).filter((order): order is OrderDownloadStateNew => !!order);

    const downloadingOrders = filterOrdersByOptions(
      allOrders,
      {
        excludeDownloadPageOrder: true,
        excludeFreebieDownloadPageOrder: true,
        excludePaygDownload: true,
      },
      stillDownloadingOrdersRef.current
    );

    return downloadingOrders.filter((order) => order.state === DownloadStateTypes.DOWNLOADING);
  };

  const prepareOrdersForApiCall = (orders: OrderDownloadStateNew[]) => {
    return orders
      .filter((order) => order.orderId && order.orderId.trim() !== '')
      .map((order) => ({
        id: order.orderId,
        percentageDone: order.downloadProgress ?? 0,
        sleepTime: 3,
      }));
  };

  const stopPolling = (): void => {
    pollingAttemptsRef.current = {};
    stillDownloadingOrdersRef.current.clear();
    isPollingRef.current = false;
  };

  const scheduleNextPoll = (): void => {
    pollingTimeoutRef.current = setTimeout(() => {
      void handlePollingForMultipleOrders();
    }, POLLING_INTERVAL);
  };

  const markOrdersAsTracking = (orders: OrderDownloadStateNew[]): void => {
    orders.forEach((order) => {
      stillDownloadingOrdersRef.current.add(order.orderIdKey);
    });
  };

  const handleOrderError = (order: OrderDownloadStateNew): void => {
    const errorUpdate = createErrorStateUpdate({
      orderIdKey: order.orderIdKey,
      orderId: order.orderId,
      name: order.name,
      fileType: order.fileType,
      isGoogleDriveDownload: order.isGoogleDriveDownload,
      isDropboxDownload: order.isDropboxDownload,
      thumbnailUrl: order.thumbnailUrl,
    });
    dispatch(
      updateOrderStateNew({
        ...errorUpdate,
        state: DownloadStateTypes.ERROR,
      } as OrderDownloadStateNew)
    );
    delete pollingAttemptsRef.current[order.orderIdKey];
    stillDownloadingOrdersRef.current.delete(order.orderIdKey);
  };

  const triggerDownloadForOrder = (order: OrderDownloadStateNew, downloadUrl: string): void => {
    if (downloadTriggeredRef.current[order.orderIdKey]) {
      return;
    }

    downloadTriggeredRef.current[order.orderIdKey] = true;
    const downloadSafePosterName = makeFilenameSafe(order.name, 'poster');

    if (order.isDropboxDownload) {
      return;
    }

    if (order.isGoogleDriveDownload) {
      void saveToGoogleDriveWithSelectedFolder(downloadUrl, downloadSafePosterName).then(() => {
        showEditorNotificationMessageGrowl(window.i18next.t('pmwjs_saved'));
      });
      return;
    }

    const downloadIndex = Object.keys(downloadTriggeredRef.current).length - 1;
    triggerDownloadWithDelay(downloadUrl, downloadSafePosterName, order.fileType, downloadIndex);
  };

  const handleOrderReady = (order: OrderDownloadStateNew, downloadUrl: string, printShopLink?: string): void => {
    triggerDownloadForOrder(order, downloadUrl);

    const readyUpdate = createReadyStateUpdate(
      {
        orderIdKey: order.orderIdKey,
        orderId: order.orderId,
        name: order.name,
        fileType: order.fileType,
        isGoogleDriveDownload: order.isGoogleDriveDownload,
        isDropboxDownload: order.isDropboxDownload,
        thumbnailUrl: order.thumbnailUrl,
        downloadResult: order.downloadResult,
      },
      downloadUrl,
      printShopLink
    );
    dispatch(
      updateOrderStateNew({
        ...readyUpdate,
        state: DownloadStateTypes.READY,
      } as OrderDownloadStateNew)
    );
    delete pollingAttemptsRef.current[order.orderIdKey];
    stillDownloadingOrdersRef.current.delete(order.orderIdKey);
  };

  const handleOrderProgress = (order: OrderDownloadStateNew, progress: number): void => {
    const downloadingUpdate = createDownloadingStateUpdate(
      {
        orderIdKey: order.orderIdKey,
        orderId: order.orderId,
        name: order.name,
        fileType: order.fileType,
        isGoogleDriveDownload: order.isGoogleDriveDownload,
        isDropboxDownload: order.isDropboxDownload,
        thumbnailUrl: order.thumbnailUrl,
        downloadResult: order.downloadResult,
      },
      progress
    );
    dispatch(
      updateOrderStateNew({
        ...downloadingUpdate,
        state: DownloadStateTypes.DOWNLOADING,
      } as OrderDownloadStateNew)
    );

    pollingAttemptsRef.current[order.orderIdKey] = (pollingAttemptsRef.current[order.orderIdKey] ?? 0) + 1;

    if (pollingAttemptsRef.current[order.orderIdKey] >= MAX_POLLING_ATTEMPTS) {
      handleOrderError(order);
    }
  };

  const processProgressResults = (orders: OrderDownloadStateNew[], progressResults: MultipleOrderProgressResponse): void => {
    for (const order of orders) {
      const progress = progressResults[order.orderId];

      if (!progress) {
        continue;
      }

      if (progress.error) {
        handleOrderError(order);
        continue;
      }

      if (progress.percentageDone === 100 && progress.downloadUrl) {
        handleOrderReady(order, progress.downloadUrl, progress.printShopLink);
        continue;
      }

      handleOrderProgress(order, progress.percentageDone);
    }
  };

  const handlePollingForMultipleOrders = async (): Promise<void> => {
    isPollingRef.current = true;

    const ordersStillDownloading = getOrdersToPoll();

    if (ordersStillDownloading.length === 0) {
      stopPolling();
      return;
    }

    const ordersToCheck = prepareOrdersForApiCall(ordersStillDownloading);

    if (ordersToCheck.length === 0) {
      scheduleNextPoll();
      return;
    }

    markOrdersAsTracking(ordersStillDownloading);

    try {
      const progressResults = await getMultipleOrdersProgress(ordersToCheck);
      processProgressResults(ordersStillDownloading, progressResults);

      if (stillDownloadingOrdersRef.current.size > 0) {
        scheduleNextPoll();
      } else {
        isPollingRef.current = false;
      }
    } catch (error) {
      console.error('Error polling for order progress:', error);

      for (const order of ordersStillDownloading) {
        handleOrderError(order);
      }
      stopPolling();
    }
  };

  useEffect(() => {
    if (!isUserPremium()) {
      return;
    }

    const {orderStateHashmapNew: latestHashmap} = getLatestState.current();
    const downloadingOrders = currentlyProcessingOrderIdKeys.map((key) => latestHashmap[key]).filter((order) => order && order.state === DownloadStateTypes.DOWNLOADING);
    if (downloadingOrders.length > 0 && !isPollingRef.current) {
      void handlePollingForMultipleOrders();
    }

    return (): void => {
      if (!isUserPremium()) {
        return;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
      isPollingRef.current = false;
    };
  }, [pollingTrigger]);
};
